<template>
  <section v-if="loading">
    <b-row class="mx-1 mb-25">
      <b-skeleton
        width="10%"
      />
    </b-row>
    <b-container class="container-small mt-2 mb-4">
      <b-card>
        <b-skeleton
          width="20%"
          height="14px"
          class="mb-1 mt-50"
        />
        <b-skeleton
          height="42px"
          class="mb-2"
        />
      </b-card>
    </b-container>
    <b-container class="mw-100 mt-5">
      <b-card class="pl-1">
        <b-row class="mb-1">
          <b-skeleton
            width="10%"
          />
        </b-row>
        <b-row>
          <b-skeleton
            height="42px"
            width="15%"
            class="mr-50"
          />
          <b-skeleton
            height="42px"
            width="15%"
            class="mr-50"
          />
          <b-skeleton
            height="42px"
            width="65%"
            class="mr-50"
          />
        </b-row>
      </b-card>
    </b-container>
  </section>
  <section v-else>
    <scroll-container
      height-offset="155px"
    >
      <div class="border-bottom mb-2">
        <h6>{{ $tk('ClientPortal.Catalog.CatalogSet') }}</h6>
      </div>
      <b-container class="container-small">
        <b-card>
          <b-form-group
            class="mb-0"
            :label="$tk('ClientPortal.Catalog.SetName') + ' *'"
            :invalid-feedback="errors['Name']"
          >
            <b-form-input
              id="Name"
              v-model="wrapper.setModel.name"
              type="text"
              :placeholder="$tk('ClientPortal.Catalog.Set.NameYourSet')"
              :state="errors['Name'] == null ? null : false"
            />
          </b-form-group>
        </b-card>
      </b-container>
      <div class="border-bottom mb-2">
        <h6>
          {{ $tk('ClientPortal.Catalog.Set.AddConditions') }}
        </h6>
      </div>
      <b-form-group
        v-for="(filter, index) in wrapper.setModel.filters"
        :key="index"
      >
        <div
          v-if="index !== 0"
          class="text-center mb-1"
        >
          <div class="d-inline-block card-shadow bg-white p-50 rounded-semi-circle dotted-line-x">
            <div
              :class="{'bg-primary-light' : logicalOperator == 1}"
              class="d-inline-block bg-light py-0 px-50 rounded-semi-circle"
              @click="filter.logicalOperator = 1"
            >
              <b-form-radio
                v-model="filter.logicalOperator"
                value="1"
              >
                {{ $tk('ClientPortal.Catalog.LogicalOperator.AND') }}
              </b-form-radio>
            </div>
            <div
              :class="{'bg-primary-light' : logicalOperator == 2}"
              class="d-inline-block bg-light py-0 px-50 rounded-semi-circle ml-2"
              @click="filter.logicalOperator = 2"
            >
              <b-form-radio
                v-model="filter.logicalOperator"
                value="2"
              >
                {{ $tk('ClientPortal.Catalog.LogicalOperator.OR') }}
              </b-form-radio>
            </div>
            <div
              :class="{'bg-primary-light' : logicalOperator == 3}"
              class="d-inline-block bg-light py-0 px-50 rounded-semi-circle ml-2"
              @click="filter.logicalOperator = 3"
            >
              <b-form-radio
                v-model="filter.logicalOperator"
                value="3"
              >
                {{ $tk('ClientPortal.Catalog.LogicalOperator.NOT') }}
              </b-form-radio>
            </div>
          </div>
        </div>
        <b-card
          class="mb-0"
          header-class="py-1 border-bottom"
          body-class="pt-1"
        >
          <template
            #header
          >
            <div>
              {{ $tk('ClientPortal.Catalog.Filter') }} {{ index + 1 }}
            </div>
            <b-link
              v-if="index > 0 || wrapper.setModel.filters.length > 1"
              class="align-self-end"
              @click="removeFilter(index)"
            >
              <GjIcon
                class="mr-50"
                name="Delete"
              />
              {{ $tk('ClientPortal.Catalog.Delete') }}
            </b-link>
          </template>
          <b-form-row>
            <b-col
              sm="2"
              class="mb-50 mb-sm-0"
            >
              <v-select
                v-model="filter.columnId"
                :options="availableFilterColumns"
                label="name"
                taggable
                :clearable="false"
                select-on-tab
                :reduce="f => f.id"
                @input="(selectedOption, name) => setFilterColumnId(selectedOption, filter)"
              >
                <template
                  v-if="availableFilterColumns.length === 0"
                  #no-options
                >
                  {{ $tk('ClientPortal.CatalogSet.NoAvailableFilters') }}
                </template>
              </v-select>
            </b-col>
            <b-col
              v-if="filter.columnId !== 0"
              sm="2"
              class="mb-50 mb-sm-0"
            >
              <v-select
                v-model="filter.filterOperator"
                :options="getOptionsForFilterType(filter.parameterType)"
                label="text"
                taggable
                :clearable="false"
                select-on-tab
                :reduce="f => f.value"
              />
            </b-col>
            <b-col
              v-if="filter.filterOperator !== 0"
              sm="8"
              class="mb-50 mb-sm-0"
            >
              <template v-if="filter.parameterType === 1">
                <b-form-tags
                  v-model="filter.filterValues"
                  input-id="tags-basic"
                  add-button-variant="primary d-none"
                  tag-pills
                  tag-variant="primary-light"
                  :placeholder="$tk('ClientPortal.Catalog.Set.TagsPlaceholder')"
                  remove-on-delete
                  @input="newValues => filter.filterValues = newValues"
                />
              </template>
              <template v-else>
                <b-form-group
                  :invalid-feedback="$tk('ClientPortal.Catalog.Set.InvalidValueForFilters')"
                >
                  <b-form-input
                    v-model="filter.filterValue"
                    type="number"
                    :state="filter.filterValue.length <= 20 ? null : false"
                  />
                </b-form-group>
              </template>
            </b-col>
          </b-form-row>
        </b-card>
        <span
          v-if="errors['Filters']"
          id="Filters"
          class="field-validation-valid text-danger small mb-3"
        >
          {{ errors['Filters'] }}
        </span>
      </b-form-group>
      <div class="text-center">
        <b-button
          variant="outline-primary bg-white dotted-line-top"
          size="sm"
          class="dotted-line-top"
          @click="addFilter(1)"
        >
          <GjIcon
            class="mr-50"
            name="Plus"
          />
          {{ $tk('ClientPortal.Catalog.AddFilter') }}
        </b-button>
      </div>
      <div
        v-if="!validating"
        class="border-bottom my-2"
      >
        <h6 class="d-inline-block">
          {{ wrapper.filterResult.catalogItemsCount }} {{ $tk('ClientPortal.CatalogSet.ItemsMeetTheQuery') }}
        </h6>
        <GjIcon
          v-b-tooltip.hover="$tk('ClientPortal.CatalogSet.ItemsMeetTheQueryTooltip')"
          name="Info"
        />
      </div>
      <div class="swiper-container mb-2">
        <swiper
          class="swiper-centered-slides p-1"
          :options="swiperOptions"
          :breakpoints="swiperOptions.breakpoints"
        >
          <template v-if="!validating">
            <swiper-slide
              v-for="catalogItem in wrapper.filterResult.catalogItems"
              :key="catalogItem.id"
              class="rounded bg-white px-1 py-2 swiper-shadow"
            >
              <a
                :href="catalogItem.contentUrl"
                target="_blank"
              >
                <div
                  :style="{ backgroundImage: 'url(' + catalogItem.contentImageUrl + ')' }"
                  class="swiper-card mb-2"
                />
                <h5 class="text-secondary mb-0">{{ catalogItem.contentName }}</h5>
              </a>
            </swiper-slide>
          </template>
          <template v-if="validating">
            <swiper-slide
              v-for="i in 4"
              :key="i"
              class="rounded bg-white px-1 py-3 swiper-shadow"
            >
              <b-skeleton-img />
              <b-skeleton />
            </swiper-slide>
          </template>

          <div
            slot="button-next"
            class="swiper-button-next swiper-shadow bg-white"
          >
            <GjIcon
              class="text-secondary"
              name="ArrowRight"
              size="40px"
            />
          </div>
          <div
            slot="button-prev"
            class="swiper-button-prev swiper-shadow bg-white"
          >
            <GjIcon
              class="text-secondary"
              name="ArrowLeft"
              size="40px"
            />
          </div>
        </swiper>
      </div>
    </scroll-container>

    <div class="border-top text-right py-1">
      <b-button
        class="bg-white"
        variant="outline-dark"
        size="sm"
        @click="cancel"
      >
        {{ $tk('ClientPortal.Form.Actions.Cancel') }}
      </b-button>
      <b-button
        class="bg-white ml-50"
        variant="primary"
        size="sm"
        :disabled="saving || !isFormComplete"
        @click="saveSet(wrapper.setModel)"
      >
        {{ $tk('ClientPortal.Form.Actions.Save') }}
      </b-button>
    </div>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import CatalogSetsService from '@/api/client/catalogSets.service'
import debounce from '@/mixins/debounce'
import notification from '@/mixins/toastification'
import ScrollContainer from '@/components/ScrollContainer.vue'
import store from '@/store'
import scrollToError from '@/mixins/scrollToError'
import vSelect from 'vue-select'

const PARAMETER_TYPES = Object.freeze({
  WORD: 1,
  QUANTITY: 2,
})

export default {
  components: {
    GjIcon,
    Swiper,
    SwiperSlide,
    ScrollContainer,
    vSelect,
  },
  mixins: [debounce, notification],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    catalogId: String,
    catalogSetId: {
      type: String,
      default: '0',
    },
    adGroupId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      wrapper: {},
      loading: true,
      saving: false,
      validating: false,
      errors: {},
      value: ['apple', 'orange'],
      logicalOperator: 1,
      swiperData: [
        { icon: 'GithubIcon', text: 'Getting Started' },
      ],
      swiperOptions: {
        slidesPerView: '4',
        spaceBetween: 30,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        breakpoints: {
          1318: {
            slidesPerView: '4',
            spaceBetween: 30,
          },
          768: {
            slidesPerView: '3',
            spaceBetween: 30,
          },
          550: {
            slidesPerView: '2',
            spaceBetween: 50,
          },
          230: {
            slidesPerView: '1',
          },
        },
      },
    }
  },
  computed: {
    availableFilterColumns() {
      var parameters = this.wrapper.availableColumns
      var options = []
      parameters.forEach(availableColumn => {
        const option = {
          id: availableColumn.columnValue,
          name: availableColumn.text,
        }
        options.push(option)
      })
      return options
    },
    isSetValid() {
      const { setModel } = this.wrapper
      if (!setModel.name) return false

      if (setModel.filters.length === 0) return false

      if (setModel.filters.find(filter => filter.filterValue.length >= 20)) return false

      const isWritingTag = setModel.filters.filter(x => (Boolean(x.tag) && x.tag !== ''))
      if (isWritingTag.length > 0) return false
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < setModel.filters.length; i++) {
        const filter = setModel.filters[i]

        if (filter.columnId === 0 || filter.filterOperator === 0) return false

        if (filter.filterValues.length === 0 && filter.filterValue === '') return false

        if (filter.parameterType === PARAMETER_TYPES.WORD && filter.filterValues.length === 0) {
          return false
        }

        if (filter.parameterType === PARAMETER_TYPES.QUANTITY && filter.filterValue === '') {
          return false
        }
      }
      return true
    },
    isFormComplete() {
      const { name, filters } = this.wrapper.setModel

      return name && filters.length && filters.every(x => x.parameterType
      && x.filterOperator && (x.filterValue || x.filterValues.length)) && !filters.find(filter => filter.filterValue.length >= 20)
    },
  },
  watch: {
    'wrapper.setModel.filters': {
      handler() {
        // This has been added mainly cause of price input where user can keep typing numbers
        clearTimeout(this.timeout)
        const $this = this
        this.timeout = setTimeout(() => {
          $this.validateSet($this.wrapper.setModel, this.$route.params.accountId)
        }, 300)
      },
      deep: true,
      immediate: false,
    },
    '$route.params.accountId': 'onAccountChange',
  },
  async mounted() {
    this.loading = true

    try {
      this.wrapper = await CatalogSetsService.getCatalogSet(this.catalogId, this.catalogSetId, this.$route.params.accountId)
    } catch (err) {
      this.error(err.message)
    } finally {
      if (this.wrapper.setModel.id === 0) {
        this.addFilter(1)
      }

      this.wrapper.setModel.filters.forEach(filter => { filter.filterValues = filter.filterValues.map(x => x.text) })
      this.loading = false
    }
  },
  methods: {
    addColumnToFilter(column) {
      const copy = JSON.parse(JSON.stringify(column))
      copy.filterValues = []
      copy.logicalOperator = 1
      this.wrapper.setModel.filters.push(copy)
    },
    removeFilter(index) {
      this.wrapper.setModel.filters.splice(index, 1)
    },
    getOptionsForFilterType(parameterType) {
      return this.wrapper.columnFilters.filter(x => x.filterType === parameterType)
    },
    async saveSet(model) {
      this.saving = true
      const copy = JSON.parse(JSON.stringify(model))
      copy.filters = model.filters.map(x => ({
        columnId: x.columnId,
        filterOperator: x.filterOperator,
        filterValues: x.parameterType === PARAMETER_TYPES.WORD ? x.filterValues.map(y => ({ text: y })) : [],
        logicalOperator: x.logicalOperator,
        parameterType: x.parameterType,
        filterValue: x.parameterType === PARAMETER_TYPES.QUANTITY ? x.filterValue : '',
      }))
      copy.catalogId = this.catalogId
      copy.id = this.wrapper.setModel.id

      this.errors = {}
      const { accountId } = this.$route.params
      try {
        const response = this.catalogSetId === '0' ? await CatalogSetsService.createSet(this.catalogId, copy, accountId) : await CatalogSetsService.updateSet(this.catalogId, this.catalogSetId, copy, accountId)
        if (response.saved) {
          if (this.catalogSetId === '0') {
            store.commit('app/TOGGLE_NEW_CREATE_STATE', true)
          }
          const { adGroupId } = this.$route.query
          this.success(response.message)
          if (adGroupId) {
            this.$router.push(`/${this.$route.params.accountId}/ad/create?adGroupId=${adGroupId}&catalogSetId=${response.catalogSetEncodedId}`)
          } else {
            this.$router.push(`/${this.$route.params.accountId}/catalogs/${this.catalogId}/details#sets`)
          }
        } else {
          this.errors = response.errors
          this.error(response.message)
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        scrollToError(this.errors)
        this.saving = false
      }
    },
    async validateSet(model) {
      if (!this.isSetValid) return

      this.validating = true
      const copy = JSON.parse(JSON.stringify(model))
      copy.filters = model.filters.map(x => ({
        columnId: x.columnId,
        filterOperator: x.filterOperator,
        filterValues: x.parameterType === PARAMETER_TYPES.WORD ? x.filterValues.map(y => ({ text: y })) : [],
        logicalOperator: x.logicalOperator,
        parameterType: x.parameterType,
        filterValue: x.parameterType === PARAMETER_TYPES.QUANTITY ? x.filterValue : '',
      }))

      this.wrapper.filterResult.catalogItems = []
      copy.catalogId = this.catalogId

      try {
        const response = await CatalogSetsService.validateSet(this.catalogId, copy, this.$route.params.accountId)
        if (response.valid) {
          this.errors = {}
          this.wrapper.filterResult.catalogItemsCount = response.numberOfItems
          this.wrapper.filterResult.catalogItems = response.catalogItems
        } else {
          this.errors = response.errors
          this.error(response.message)
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        scrollToError(this.errors)
        this.validating = false
      }
    },
    getOptions(filter) {
      const parameters = this.wrapper.availableColumns
      const options = []
      parameters.forEach(availableColumn => {
        const option = {
          id: availableColumn.columnValue,
          name: availableColumn.text,
        }
        options.push(option)
      })
      filter.availableColumns = options

      return filter.availableColumns
    },
    addFilter(logicalOperator) {
      const filterModel = {
        columnId: 0,
        filterOperator: 0,
        filterValues: [],
        filterValue: '',
        logicalOperator,
        parameterType: 0,
      }
      this.wrapper.setModel.filters.push(filterModel)
    },
    setFilterColumnId(selectedOption, filter) {
      filter.filterOperator = 0
      const column = this.wrapper.availableColumns.find(x => x.columnValue === selectedOption)
      if (column != null) {
        filter.parameterType = column.parameterType
        filter.filterValue = ''
      }
    },
    addValue(newTags, filter) {
      filter.filterValues.push(...newTags)
    },
    onSetNameInput() {
      this.debounce(() => {
        if (this.wrapper.setModel.name.length === 0) { return }
        this.validateSet(this.wrapper.setModel, this.$route.params.accountId)
      }, 500)
    },
    cancel() {
      const { adGroupId } = this.$route
      if (adGroupId) {
        this.$router.push(`/${this.$route.params.accountId}/ad/create?adGroupId=${adGroupId}&catalogSetId=${this.catalogSetId}`)
      } else {
        this.$router.push(`/${this.$route.params.accountId}/catalogs/${this.catalogId}/details#sets`)
      }
    },
    onAccountChange() {
      this.$router.push(`/${this.$route.params.accountId}/catalogs`)
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/properties/catalog/create-catalog-set.scss';
.icon--catalog-set {
  right: 4px!important;
}
.custom-b-select--catalog-set {
  padding-right: 2.4rem!important;
}
</style>
